import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Paper, Box, Typography, Grid, Card, CardContent, Button, TextField,
  Autocomplete, Chip, CircularProgress, Alert, Tab, Tabs, Tooltip, IconButton,
  Modal, Divider, FormControl, InputLabel, Select, MenuItem, TextareaAutosize
} from '@mui/material';
import {
  Build, Add, Edit, Delete, PictureAsPdf, TableChart, FilterList, Clear,
  TrendingUp, TrendingDown, MonetizationOn, DirectionsCar, Schedule,
  CheckCircle, Warning, Error as ErrorIcon, History, Close, CalendarToday, Assessment
} from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API_URL = process.env.REACT_APP_API_URL;

export default function Manutencoes() {
  const [manutencoes, setManutencoes] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [modalAberto, setModalAberto] = useState(false);
  const [modalHistoricoAberto, setModalHistoricoAberto] = useState(false);
  const [modalExcluirAberto, setModalExcluirAberto] = useState(false);
  const [modalFinalizarAberto, setModalFinalizarAberto] = useState(false);
  const [criarDespesaFinanceira, setCriarDespesaFinanceira] = useState(true);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [manutencaoSelecionada, setManutencaoSelecionada] = useState(null);
  const [historicoVeiculo, setHistoricoVeiculo] = useState([]);
  const [estatisticasVeiculo, setEstatisticasVeiculo] = useState(null);
  const [filtros, setFiltros] = useState({
    data_inicial: '',
    data_final: '',
    veiculo: null,
    status: null,
    tipo: null
  });

  const [formulario, setFormulario] = useState({
    veiculo_id: '',
    tipo: 'Corretiva',
    status: 'Agendada',
    data_entrada: '',
    data_prevista_saida: '',
    data_saida: '',
    oficina_nome: '',
    oficina_contato: '',
    descricao: '',
    problema_reportado: '',
    servicos_realizados: '',
    custo_mao_obra: 0,
    custo_pecas: 0,
    custo_outros: 0,
    km_atual: '',
    proxima_revisao_km: ''
  });

  const tiposManutencao = [
    { value: 'Preventiva', label: 'Preventiva', color: 'info' },
    { value: 'Corretiva', label: 'Corretiva', color: 'warning' },
    { value: 'Revisão', label: 'Revisão', color: 'primary' },
    { value: 'Emergencial', label: 'Emergencial', color: 'error' }
  ];

  const statusOptions = [
    { value: 'Agendada', label: 'Agendada', color: 'default' },
    { value: 'Em Andamento', label: 'Em Andamento', color: 'warning' },
    { value: 'Concluída', label: 'Concluída', color: 'success' },
    { value: 'Cancelada', label: 'Cancelada', color: 'error' }
  ];

  // Carregar dados iniciais
  useEffect(() => {
    carregarVeiculos();
    carregarManutencoes();
  }, []);

  // Recarregar quando filtros mudarem
  useEffect(() => {
    carregarManutencoes();
  }, [filtros]);

  const carregarVeiculos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/veiculos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVeiculos(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar veículos:', error);
    }
  };

  const carregarManutencoes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/manutencoes`;
      const params = new URLSearchParams();

      if (filtros.data_inicial) params.append('data_inicio', filtros.data_inicial);
      if (filtros.data_final) params.append('data_fim', filtros.data_final);
      if (filtros.veiculo?.id) params.append('veiculo_id', filtros.veiculo.id);
      if (filtros.status?.value) params.append('status', filtros.status.value);
      if (filtros.tipo?.value) params.append('tipo', filtros.tipo.value);

      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setManutencoes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar manutenções:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarHistoricoVeiculo = async (veiculoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/manutencoes/veiculo/${veiculoId}/historico`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoricoVeiculo(response.data.historico || []);
      setEstatisticasVeiculo(response.data.estatisticas);
      setModalHistoricoAberto(true);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  // Estatísticas gerais
  const estatisticas = useMemo(() => {
    const total = manutencoes.length;
    const emAndamento = manutencoes.filter(m => m.status === 'Em Andamento').length;
    const agendadas = manutencoes.filter(m => m.status === 'Agendada').length;
    const concluidas = manutencoes.filter(m => m.status === 'Concluída').length;
    const canceladas = manutencoes.filter(m => m.status === 'Cancelada').length;

    const custoTotal = manutencoes.reduce((acc, m) => 
      acc + parseFloat(m.custo_mao_obra || 0) + parseFloat(m.custo_pecas || 0) + parseFloat(m.custo_outros || 0), 0
    );

    const mediaDias = manutencoes.filter(m => m.dias_parado).length > 0
      ? manutencoes.reduce((acc, m) => acc + (m.dias_parado || 0), 0) / manutencoes.filter(m => m.dias_parado).length
      : 0;

    const veiculosEmManutencao = manutencoes.filter(m => m.status === 'Em Andamento')
      .map(m => ({ id: m.veiculo_id, placa: m.veiculo_placa, modelo: m.veiculo_modelo }));

    return {
      total,
      emAndamento,
      agendadas,
      concluidas,
      canceladas,
      custoTotal,
      mediaDias,
      veiculosEmManutencao
    };
  }, [manutencoes]);

  // Análise por tipo
  const analisePorTipo = useMemo(() => {
    const tipos = {};
    manutencoes.forEach(m => {
      if (!tipos[m.tipo]) {
        tipos[m.tipo] = { quantidade: 0, custo: 0 };
      }
      tipos[m.tipo].quantidade++;
      tipos[m.tipo].custo += parseFloat(m.custo_mao_obra || 0) + 
                              parseFloat(m.custo_pecas || 0) + 
                              parseFloat(m.custo_outros || 0);
    });
    return Object.entries(tipos).map(([tipo, dados]) => ({ tipo, ...dados }));
  }, [manutencoes]);

  // Veículos com mais manutenções
  const topVeiculos = useMemo(() => {
    const veiculosMap = {};
    manutencoes.forEach(m => {
      if (!veiculosMap[m.veiculo_id]) {
        veiculosMap[m.veiculo_id] = {
          placa: m.veiculo_placa,
          modelo: m.veiculo_modelo,
          marca: m.veiculo_marca,
          quantidade: 0,
          custo: 0
        };
      }
      veiculosMap[m.veiculo_id].quantidade++;
      veiculosMap[m.veiculo_id].custo += parseFloat(m.custo_mao_obra || 0) + 
                                          parseFloat(m.custo_pecas || 0) + 
                                          parseFloat(m.custo_outros || 0);
    });
    return Object.values(veiculosMap).sort((a, b) => b.quantidade - a.quantidade).slice(0, 5);
  }, [manutencoes]);

  const formatarValor = (valor) => {
    return `R$ ${parseFloat(valor || 0).toFixed(2).replace('.', ',')}`;
  };

  const formatarData = (data) => {
    return data ? new Date(data).toLocaleDateString('pt-BR') : '-';
  };

  const limparFiltros = () => {
    setFiltros({
      data_inicial: '',
      data_final: '',
      veiculo: null,
      status: null,
      tipo: null
    });
  };

  const abrirModalNovo = () => {
    setModoEdicao(false);
    setFormulario({
      veiculo_id: '',
      tipo: 'Corretiva',
      status: 'Agendada',
      data_entrada: new Date().toISOString().split('T')[0],
      data_prevista_saida: '',
      data_saida: '',
      oficina_nome: '',
      oficina_contato: '',
      descricao: '',
      problema_reportado: '',
      servicos_realizados: '',
      custo_mao_obra: 0,
      custo_pecas: 0,
      custo_outros: 0,
      km_atual: '',
      proxima_revisao_km: ''
    });
    setModalAberto(true);
  };

  const abrirModalEdicao = (manutencao) => {
    setModoEdicao(true);
    setManutencaoSelecionada(manutencao);
    setFormulario({
      veiculo_id: manutencao.veiculo_id,
      tipo: manutencao.tipo,
      status: manutencao.status,
      data_entrada: manutencao.data_entrada,
      data_prevista_saida: manutencao.data_prevista_saida || '',
      data_saida: manutencao.data_saida || '',
      oficina_nome: manutencao.oficina_nome || '',
      oficina_contato: manutencao.oficina_contato || '',
      descricao: manutencao.descricao || '',
      problema_reportado: manutencao.problema_reportado || '',
      servicos_realizados: manutencao.servicos_realizados || '',
      custo_mao_obra: manutencao.custo_mao_obra || 0,
      custo_pecas: manutencao.custo_pecas || 0,
      custo_outros: manutencao.custo_outros || 0,
      km_atual: manutencao.km_atual || '',
      proxima_revisao_km: manutencao.proxima_revisao_km || ''
    });
    setModalAberto(true);
  };

  const confirmarExclusao = (manutencao) => {
    setManutencaoSelecionada(manutencao);
    setModalExcluirAberto(true);
  };

  const salvarManutencao = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (modoEdicao) {
        await axios.put(
          `${API_URL}/manutencoes/${manutencaoSelecionada.id}`,
          formulario,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Mensagem informativa sobre status do veículo
        if (formulario.status === 'Em Andamento') {
          alert('✅ Manutenção atualizada! O veículo foi marcado como "Em Manutenção" e não estará disponível para locação.');
        } else if (formulario.status === 'Concluída' || formulario.status === 'Cancelada') {
          alert('✅ Manutenção atualizada! O veículo voltou para o status "Disponível" e pode ser alugado novamente.');
        } else {
          alert('✅ Manutenção atualizada com sucesso!');
        }
      } else {
        await axios.post(
          `${API_URL}/manutencoes`,
          formulario,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Mensagem informativa sobre status do veículo
        if (formulario.status === 'Em Andamento') {
          alert('✅ Manutenção criada! O veículo foi marcado como "Em Manutenção" e não estará disponível para locação.');
        } else {
          alert('✅ Manutenção criada com sucesso!');
        }
      }

      setModalAberto(false);
      carregarManutencoes();
    } catch (error) {
      console.error('Erro ao salvar manutenção:', error);
      alert('❌ Erro ao salvar manutenção: ' + (error.response?.data?.mensagem || error.message));
    }
  };

  const excluirManutencao = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/manutencoes/${manutencaoSelecionada.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setModalExcluirAberto(false);
      carregarManutencoes();
    } catch (error) {
      console.error('Erro ao excluir manutenção:', error);
      alert('Erro ao excluir manutenção');
    }
  };

  const abrirModalFinalizar = (manutencao) => {
    setManutencaoSelecionada(manutencao);
    setFormulario({
      veiculo_id: manutencao.veiculo_id,
      tipo: manutencao.tipo,
      status: manutencao.status,
      data_entrada: manutencao.data_entrada,
      data_prevista_saida: manutencao.data_prevista_saida || '',
      data_saida: new Date().toISOString().split('T')[0],
      oficina_nome: manutencao.oficina_nome || '',
      oficina_contato: manutencao.oficina_contato || '',
      descricao: manutencao.descricao || '',
      problema_reportado: manutencao.problema_reportado || '',
      servicos_realizados: manutencao.servicos_realizados || '',
      custo_mao_obra: manutencao.custo_mao_obra || 0,
      custo_pecas: manutencao.custo_pecas || 0,
      custo_outros: manutencao.custo_outros || 0,
      km_atual: manutencao.km_atual || '',
      proxima_revisao_km: manutencao.proxima_revisao_km || ''
    });
    setCriarDespesaFinanceira(true);
    setModalFinalizarAberto(true);
  };

  const finalizarManutencao = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Finalizar manutenção
      await axios.post(
        `${API_URL}/manutencoes/${manutencaoSelecionada.id}/finalizar`,
        {
          data_saida: formulario.data_saida,
          servicos_realizados: formulario.servicos_realizados,
          custo_mao_obra: formulario.custo_mao_obra,
          custo_pecas: formulario.custo_pecas,
          custo_outros: formulario.custo_outros
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Se marcado, criar despesa no módulo financeiro
      if (criarDespesaFinanceira) {
        const custoTotal = parseFloat(formulario.custo_mao_obra || 0) + 
                          parseFloat(formulario.custo_pecas || 0) + 
                          parseFloat(formulario.custo_outros || 0);
        
        if (custoTotal > 0) {
          try {
            await axios.post(
              `${API_URL}/contas-pagar`,
              {
                descricao: `Manutenção - ${manutencaoSelecionada.veiculo_placa} (${manutencaoSelecionada.tipo})`,
                valor: custoTotal,
                data_vencimento: formulario.data_saida,
                categoria: 'Manutenção',
                fornecedor: manutencaoSelecionada.oficina_nome || 'Oficina',
                observacoes: `Mão de obra: R$ ${formulario.custo_mao_obra} | Peças: R$ ${formulario.custo_pecas} | Outros: R$ ${formulario.custo_outros}\n${formulario.servicos_realizados || ''}`,
                status: 'Pendente'
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            console.log('✅ Despesa financeira criada com sucesso');
          } catch (financeiroError) {
            console.error('⚠️ Erro ao criar despesa financeira:', financeiroError);
            alert('Manutenção finalizada, mas houve erro ao criar despesa financeira');
          }
        }
      }

      setModalFinalizarAberto(false);
      carregarManutencoes();
      alert('✅ Manutenção finalizada com sucesso! Veículo disponível para locação.');
    } catch (error) {
      console.error('Erro ao finalizar manutenção:', error);
      alert('Erro ao finalizar manutenção: ' + (error.response?.data?.mensagem || error.message));
    }
  };

  const exportarPDF = () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const dataAtual = new Date().toLocaleString('pt-BR');

    // ===== CABEÇALHO =====
    let yPos = 15;
    
    doc.setFillColor(31, 31, 31);
    doc.rect(0, 0, pageWidth, 25, 'F');
    
    doc.setTextColor(247, 139, 0);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('EASY LOCADORA', pageWidth / 2, 12, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text('RELATORIO DE MANUTENCOES', pageWidth / 2, 20, { align: 'center' });

    yPos = 32;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Gerado em: ${dataAtual}`, pageWidth - 15, yPos, { align: 'right' });

    yPos = 40;

    // ===== FILTROS APLICADOS =====
    if (filtros.data_inicial || filtros.data_final || filtros.veiculo || filtros.status || filtros.tipo) {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(247, 139, 0);
      doc.text('FILTROS APLICADOS:', 15, yPos);
      
      yPos += 6;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      
      if (filtros.data_inicial && filtros.data_final) {
        doc.text(`Periodo: ${formatarData(filtros.data_inicial)} a ${formatarData(filtros.data_final)}`, 15, yPos);
        yPos += 5;
      }
      if (filtros.veiculo) {
        doc.text(`Veiculo: ${filtros.veiculo.placa} - ${filtros.veiculo.modelo}`, 15, yPos);
        yPos += 5;
      }
      if (filtros.tipo) {
        doc.text(`Tipo: ${filtros.tipo.label}`, 15, yPos);
        yPos += 5;
      }
      if (filtros.status) {
        doc.text(`Status: ${filtros.status.label}`, 15, yPos);
        yPos += 5;
      }
      yPos += 5;
    }

    // ===== RESUMO ESTATÍSTICO =====
    doc.setFillColor(247, 139, 0);
    doc.rect(15, yPos - 5, pageWidth - 30, 8, 'F');
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('RESUMO ESTATISTICO', pageWidth / 2, yPos, { align: 'center' });

    yPos += 10;
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');

    // Estatísticas em 3 colunas
    const col1X = 15;
    const col2X = pageWidth / 3 + 10;
    const col3X = (pageWidth / 3) * 2 + 5;
    let currentY = yPos;

    // Coluna 1
    doc.setFont('helvetica', 'bold');
    doc.text('Total de Manutencoes:', col1X, currentY);
    doc.setFont('helvetica', 'normal');
    doc.text(String(estatisticas.total), col1X + 50, currentY);
    currentY += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Em Andamento:', col1X, currentY);
    doc.setFont('helvetica', 'normal');
    doc.text(String(estatisticas.emAndamento), col1X + 50, currentY);

    // Coluna 2
    currentY = yPos;
    doc.setFont('helvetica', 'bold');
    doc.text('Agendadas:', col2X, currentY);
    doc.setFont('helvetica', 'normal');
    doc.text(String(estatisticas.agendadas), col2X + 30, currentY);
    currentY += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Concluidas:', col2X, currentY);
    doc.setFont('helvetica', 'normal');
    doc.text(String(estatisticas.concluidas), col2X + 30, currentY);

    // Coluna 3
    currentY = yPos;
    doc.setFont('helvetica', 'bold');
    doc.text('Custo Total:', col3X, currentY);
    doc.setFont('helvetica', 'normal');
    doc.text(formatarValor(estatisticas.custoTotal), col3X + 30, currentY);
    currentY += 5;

    doc.setFont('helvetica', 'bold');
    doc.text('Media Dias Parado:', col3X, currentY);
    doc.setFont('helvetica', 'normal');
    doc.text(estatisticas.mediaDias.toFixed(1), col3X + 40, currentY);

    yPos += 15;

    // ===== TABELA DE MANUTENÇÕES =====
    const tableColumns = ['Veiculo', 'Tipo', 'Status', 'Data Entrada', 'Dias Parado', 'Custo Total'];
    const tableData = manutencoes.map(m => [
      `${m.veiculo_placa || ''} - ${m.veiculo_modelo || ''}`,
      m.tipo,
      m.status,
      formatarData(m.data_entrada),
      m.dias_parado || '-',
      formatarValor((parseFloat(m.custo_mao_obra || 0) + parseFloat(m.custo_pecas || 0) + parseFloat(m.custo_outros || 0)))
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [tableColumns],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [247, 139, 0],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: 2
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { left: 15, right: 15 },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 30, halign: 'center' },
        2: { cellWidth: 35, halign: 'center' },
        3: { cellWidth: 30, halign: 'center' },
        4: { cellWidth: 25, halign: 'center' },
        5: { cellWidth: 35, halign: 'right' }
      }
    });

    // Rodapé
    const finalY = doc.lastAutoTable.finalY + 10;
    if (finalY < pageHeight - 15) {
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'italic');
      doc.text(
        `Relatorio gerado automaticamente pelo sistema Easy Locadora - ${dataAtual}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    const dataArquivo = new Date().toISOString().split('T')[0];
    doc.save(`relatorio-manutencoes-${dataArquivo}.pdf`);
  };

  const exportarExcel = () => {
    const dataAtual = new Date().toLocaleString('pt-BR');

    // ===== CABEÇALHO =====
    const cabecalho = [
      ['EASY LOCADORA'],
      ['RELATORIO DE MANUTENCOES'],
      [`Gerado em: ${dataAtual}`],
      [''],
    ];

    // ===== FILTROS =====
    const filtrosAplicados = [['FILTROS APLICADOS:']];
    if (filtros.data_inicial && filtros.data_final) {
      filtrosAplicados.push([`Periodo: ${formatarData(filtros.data_inicial)} a ${formatarData(filtros.data_final)}`]);
    }
    if (filtros.veiculo) {
      filtrosAplicados.push([`Veiculo: ${filtros.veiculo.placa} - ${filtros.veiculo.modelo}`]);
    }
    if (filtros.tipo) {
      filtrosAplicados.push([`Tipo: ${filtros.tipo.label}`]);
    }
    if (filtros.status) {
      filtrosAplicados.push([`Status: ${filtros.status.label}`]);
    }
    filtrosAplicados.push(['']);

    // ===== RESUMO ESTATÍSTICO =====
    const resumo = [
      ['RESUMO ESTATISTICO'],
      ['Total de Manutencoes:', estatisticas.total],
      ['Em Andamento:', estatisticas.emAndamento],
      ['Agendadas:', estatisticas.agendadas],
      ['Concluidas:', estatisticas.concluidas],
      ['Custo Total:', formatarValor(estatisticas.custoTotal)],
      ['Media Dias Parado:', estatisticas.mediaDias.toFixed(1)],
      [''],
    ];

    // ===== TABELA DE MANUTENÇÕES =====
    const tabelaHeader = ['Veiculo', 'Tipo', 'Status', 'Data Entrada', 'Data Saida', 'Dias Parado', 'Oficina', 'Descricao', 'Custo Mao Obra', 'Custo Pecas', 'Custo Outros', 'Custo Total'];
    const tabelaBody = manutencoes.map(m => [
      `${m.veiculo_placa} - ${m.veiculo_modelo}`,
      m.tipo,
      m.status,
      formatarData(m.data_entrada),
      formatarData(m.data_saida),
      m.dias_parado || '',
      m.oficina_nome || '',
      m.descricao || '',
      formatarValor(m.custo_mao_obra || 0),
      formatarValor(m.custo_pecas || 0),
      formatarValor(m.custo_outros || 0),
      formatarValor((parseFloat(m.custo_mao_obra || 0) + parseFloat(m.custo_pecas || 0) + parseFloat(m.custo_outros || 0)))
    ]);

    const tabelaManutencoes = [
      ['MANUTENCOES:'],
      tabelaHeader,
      ...tabelaBody,
      [''],
    ];

    // ===== MONTAGEM FINAL =====
    const dadosCompletos = [
      ...cabecalho,
      ...filtrosAplicados,
      ...resumo,
      ...tabelaManutencoes
    ];

    const ws = XLSX.utils.aoa_to_sheet(dadosCompletos);

    // Larguras das colunas
    ws['!cols'] = [
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 12 },
      { wch: 25 },
      { wch: 30 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Manutencoes');

    const dataArquivo = new Date().toISOString().split('T')[0];
    const nomeArquivo = `relatorio-manutencoes-${dataArquivo}.xlsx`;
    const buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    saveAs(new Blob([buffer]), nomeArquivo);
  };

  // Colunas da tabela por aba
  const colunas = useMemo(() => {
    if (tabIndex === 0) {
      // Todas as manutenções
      return [
        {
          accessorKey: 'veiculo_placa',
          header: 'Veículo',
          size: 100,
          Cell: ({ row }) => (
            <Box>
              <Typography variant="body2" fontWeight="bold">{row.original.veiculo_placa}</Typography>
              <Typography variant="caption" color="text.secondary">{row.original.veiculo_modelo}</Typography>
            </Box>
          )
        },
        {
          accessorKey: 'tipo',
          header: 'Tipo',
          size: 120,
          Cell: ({ cell }) => {
            const tipo = tiposManutencao.find(t => t.value === cell.getValue());
            return <Chip label={cell.getValue()} color={tipo?.color || 'default'} size="small" />;
          }
        },
        {
          accessorKey: 'status',
          header: 'Status',
          size: 140,
          Cell: ({ cell }) => {
            const status = statusOptions.find(s => s.value === cell.getValue());
            return <Chip label={cell.getValue()} color={status?.color || 'default'} size="small" />;
          }
        },
        {
          accessorKey: 'data_entrada',
          header: 'Data Entrada',
          size: 110,
          Cell: ({ cell }) => formatarData(cell.getValue())
        },
        {
          accessorKey: 'dias_parado',
          header: 'Dias',
          size: 70,
          Cell: ({ cell }) => (
            <Chip 
              label={cell.getValue() || '0'} 
              color={cell.getValue() > 7 ? 'error' : cell.getValue() > 3 ? 'warning' : 'success'}
              size="small"
            />
          )
        },
        {
          accessorKey: 'oficina_nome',
          header: 'Oficina',
          size: 150,
          Cell: ({ cell }) => cell.getValue() || '-'
        },
        {
          accessorKey: 'custo_total',
          header: 'Custo Total',
          size: 110,
          Cell: ({ row }) => {
            const total = parseFloat(row.original.custo_mao_obra || 0) + 
                         parseFloat(row.original.custo_pecas || 0) + 
                         parseFloat(row.original.custo_outros || 0);
            return (
              <Typography fontWeight="bold" color="error">
                {formatarValor(total)}
              </Typography>
            );
          }
        },
        {
          accessorKey: 'acoes',
          header: 'Ações',
          size: 160,
          Cell: ({ row }) => (
            <Box display="flex" gap={1}>
              {row.original.status === 'Em Andamento' && (
                <Tooltip title="Finalizar">
                  <IconButton size="small" onClick={() => abrirModalFinalizar(row.original)} color="success">
                    <CheckCircle />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Editar">
                <IconButton size="small" onClick={() => abrirModalEdicao(row.original)} color="primary">
                  <Edit />
                </IconButton>
              </Tooltip>
              <Tooltip title="Histórico">
                <IconButton size="small" onClick={() => carregarHistoricoVeiculo(row.original.veiculo_id)} color="info">
                  <History />
                </IconButton>
              </Tooltip>
              <Tooltip title="Excluir">
                <IconButton size="small" onClick={() => confirmarExclusao(row.original)} color="error">
                  <Delete />
                </IconButton>
              </Tooltip>
            </Box>
          )
        }
      ];
    } else if (tabIndex === 1) {
      // Por tipo
      return [
        {
          accessorKey: 'tipo',
          header: 'Tipo de Manutenção',
          size: 180
        },
        {
          accessorKey: 'quantidade',
          header: 'Quantidade',
          size: 100,
          Cell: ({ cell }) => <Chip label={cell.getValue()} color="primary" size="small" />
        },
        {
          accessorKey: 'custo',
          header: 'Custo Total',
          size: 120,
          Cell: ({ cell }) => (
            <Typography fontWeight="bold" color="error">
              {formatarValor(cell.getValue())}
            </Typography>
          )
        }
      ];
    } else {
      // Top veículos
      return [
        {
          accessorKey: 'placa',
          header: 'Placa',
          size: 100
        },
        {
          accessorKey: 'modelo',
          header: 'Modelo',
          size: 150
        },
        {
          accessorKey: 'marca',
          header: 'Marca',
          size: 120
        },
        {
          accessorKey: 'quantidade',
          header: 'Manutenções',
          size: 120,
          Cell: ({ cell }) => <Chip label={cell.getValue()} color="warning" size="small" />
        },
        {
          accessorKey: 'custo',
          header: 'Custo Total',
          size: 130,
          Cell: ({ cell }) => (
            <Typography fontWeight="bold" color="error">
              {formatarValor(cell.getValue())}
            </Typography>
          )
        }
      ];
    }
  }, [tabIndex]);

  const dadosTabela = useMemo(() => {
    if (tabIndex === 0) return manutencoes;
    if (tabIndex === 1) return analisePorTipo;
    return topVeiculos;
  }, [tabIndex, manutencoes, analisePorTipo, topVeiculos]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* HEADER */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "#f78b00" }}>
            🔧 Gestão de Manutenções
          </Typography>
          
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={abrirModalNovo}
            >
              Nova Manutenção
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<PictureAsPdf />}
              onClick={exportarPDF}
              disabled={manutencoes.length === 0}
            >
              Exportar PDF
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<TableChart />}
              onClick={exportarExcel}
              disabled={manutencoes.length === 0}
            >
              Exportar Excel
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* FILTROS */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <FilterList color="primary" />
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Filtros
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              label="Data Inicial"
              type="date"
              fullWidth
              size="small"
              value={filtros.data_inicial}
              onChange={(e) => setFiltros({ ...filtros, data_inicial: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              label="Data Final"
              type="date"
              fullWidth
              size="small"
              value={filtros.data_final}
              onChange={(e) => setFiltros({ ...filtros, data_final: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Autocomplete
              options={veiculos}
              getOptionLabel={(option) => `${option.placa} - ${option.modelo}`}
              value={filtros.veiculo}
              onChange={(e, newValue) => setFiltros({ ...filtros, veiculo: newValue })}
              renderInput={(params) => (
                <TextField {...params} label="Veículo" size="small" />
              )}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Autocomplete
              options={tiposManutencao}
              getOptionLabel={(option) => option.label}
              value={filtros.tipo}
              onChange={(e, newValue) => setFiltros({ ...filtros, tipo: newValue })}
              renderInput={(params) => (
                <TextField {...params} label="Tipo" size="small" />
              )}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Autocomplete
              options={statusOptions}
              getOptionLabel={(option) => option.label}
              value={filtros.status}
              onChange={(e, newValue) => setFiltros({ ...filtros, status: newValue })}
              renderInput={(params) => (
                <TextField {...params} label="Status" size="small" />
              )}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={12}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<Clear />}
              onClick={limparFiltros}
              fullWidth
            >
              Limpar Filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ESTATÍSTICAS */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 2, borderTop: '4px solid #2196f3' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total de Manutenções
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {estatisticas.total}
                  </Typography>
                </Box>
                <Build sx={{ fontSize: 48, color: '#2196f3', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 2, borderTop: '4px solid #ff9800' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Em Andamento
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {estatisticas.emAndamento}
                  </Typography>
                </Box>
                <Warning sx={{ fontSize: 48, color: '#ff9800', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 2, borderTop: '4px solid #4caf50' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Concluídas
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {estatisticas.concluidas}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 48, color: '#4caf50', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 2, borderTop: '4px solid #f44336' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Custo Total
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="error.main">
                    {formatarValor(estatisticas.custoTotal)}
                  </Typography>
                </Box>
                <MonetizationOn sx={{ fontSize: 48, color: '#f44336', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Média Dias Parado
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="warning.main">
                    {estatisticas.mediaDias.toFixed(1)} dias
                  </Typography>
                  {estatisticas.mediaDias > 7 && (
                    <Chip 
                      label="Alto tempo de parada" 
                      color="error" 
                      size="small" 
                      icon={<TrendingUp />}
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
                <Schedule sx={{ fontSize: 48, color: '#ff9800', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Agendadas
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="info.main">
                    {estatisticas.agendadas}
                  </Typography>
                </Box>
                <CalendarToday sx={{ fontSize: 48, color: '#2196f3', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Canceladas
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="error.main">
                    {estatisticas.canceladas}
                  </Typography>
                </Box>
                <ErrorIcon sx={{ fontSize: 48, color: '#f44336', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ALERTAS */}
      {estatisticas.emAndamento > 0 && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            ⚠️ {estatisticas.emAndamento} veículo(s) em manutenção
          </Typography>
          <Box sx={{ mt: 1 }}>
            {estatisticas.veiculosEmManutencao.map((v, index) => (
              <Chip 
                key={index}
                label={`${v.placa} - ${v.modelo}`}
                color="warning"
                size="small"
                icon={<DirectionsCar />}
                sx={{ mr: 1, mb: 0.5 }}
              />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Estes veículos não estão disponíveis para locação no momento
          </Typography>
        </Alert>
      )}

      {estatisticas.agendadas > 0 && (
        <Alert severity="info" icon={<Schedule />} sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <Typography variant="body2">
            📅 <strong>{estatisticas.agendadas}</strong> manutenção(ões) agendada(s) para os próximos dias
          </Typography>
        </Alert>
      )}

      {/* TABELAS */}
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
        <Tabs
          value={tabIndex}
          onChange={(e, newValue) => setTabIndex(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          <Tab label="Todas as Manutenções" icon={<Build />} iconPosition="start" />
          <Tab label="Por Tipo" icon={<Assessment />} iconPosition="start" />
          <Tab label="Top Veículos" icon={<DirectionsCar />} iconPosition="start" />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <MaterialReactTable
            columns={colunas}
            data={dadosTabela}
            localization={MRT_Localization_PT_BR}
            enableColumnOrdering
            enableSorting
            enablePagination
            enableColumnFilters
            enableGlobalFilter
            enableDensityToggle
            initialState={{
              density: 'compact',
              pagination: { pageSize: 10 }
            }}
            muiTablePaperProps={{
              elevation: 0,
              sx: { borderRadius: 0 }
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: '#f5f5f5',
                fontWeight: 'bold',
                color: '#333'
              }
            }}
          />
        )}
      </Paper>

      {/* MODAL CRIAR/EDITAR MANUTENÇÃO */}
      <Modal open={modalAberto} onClose={() => setModalAberto(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '90%', md: '85%', lg: 1000 },
          maxWidth: 1200,
          maxHeight: '92vh',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ 
            p: 3, 
            borderBottom: '2px solid #f78b00', 
            position: 'sticky', 
            top: 0, 
            bgcolor: 'background.paper', 
            zIndex: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', color: '#f78b00' }}>
                <Build /> {modoEdicao ? 'Editar Manutenção' : 'Nova Manutenção'}
              </Typography>
              <IconButton onClick={() => setModalAberto(false)} size="large">
                <Close />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ 
            p: 3, 
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555'
            }
          }}>
            {/* ALERTA DE STATUS */}
            {formulario.status === 'Em Andamento' && (
              <Alert severity="warning" icon={<Warning />} sx={{ mb: 3, borderRadius: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  ⚠️ Atenção: Veículo será bloqueado para locação
                </Typography>
                <Typography variant="caption">
                  Com o status "Em Andamento", o veículo será automaticamente marcado como "Em Manutenção" 
                  e não estará disponível para novos aluguéis até a conclusão.
                </Typography>
              </Alert>
            )}
            
            {(formulario.status === 'Concluída' || formulario.status === 'Cancelada') && modoEdicao && (
              <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3, borderRadius: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  ✅ Veículo será liberado automaticamente
                </Typography>
                <Typography variant="caption">
                  Com o status "{formulario.status}", o veículo voltará para "Disponível" e poderá ser alugado novamente.
                </Typography>
              </Alert>
            )}

            {/* SEÇÃO: Identificação */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem' }}>
                <DirectionsCar /> Identificação do Veículo
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={veiculos}
                    getOptionLabel={(option) => `${option.placa} - ${option.modelo} (${option.marca})`}
                    value={veiculos.find(v => v.id === formulario.veiculo_id) || null}
                    onChange={(e, newValue) => setFormulario({ ...formulario, veiculo_id: newValue?.id || '' })}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Veículo *" 
                        placeholder="Selecione o veículo" 
                        required
                        fullWidth
                        size="medium"
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} style={{ padding: '14px 16px', borderBottom: '1px solid #f0f0f0' }}>
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1rem', mb: 0.5 }}>
                            {option.placa}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                            {option.modelo} - {option.marca} {option.ano ? `(${option.ano})` : ''}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    ListboxProps={{
                      style: { 
                        maxHeight: 400,
                        fontSize: '1rem'
                      }
                    }}
                    disabled={modoEdicao}
                  />
                  {modoEdicao && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      💡 O veículo não pode ser alterado após criação
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* SEÇÃO: Dados da Manutenção */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem' }}>
                <Build /> Dados da Manutenção
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="medium">
                    <InputLabel>Tipo *</InputLabel>
                    <Select
                      value={formulario.tipo}
                      label="Tipo *"
                      onChange={(e) => setFormulario({ ...formulario, tipo: e.target.value })}
                    >
                      {tiposManutencao.map(tipo => (
                        <MenuItem key={tipo.value} value={tipo.value}>
                          <Chip label={tipo.label} color={tipo.color} size="small" sx={{ mr: 1 }} />
                          {tipo.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="medium">
                    <InputLabel>Status *</InputLabel>
                    <Select
                      value={formulario.status}
                      label="Status *"
                      onChange={(e) => setFormulario({ ...formulario, status: e.target.value })}
                    >
                      {statusOptions.map(status => (
                        <MenuItem key={status.value} value={status.value}>
                          <Chip label={status.label} color={status.color} size="small" sx={{ mr: 1 }} />
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="KM Atual"
                    type="number"
                    size="medium"
                    value={formulario.km_atual}
                    onChange={(e) => setFormulario({ ...formulario, km_atual: e.target.value })}
                    InputProps={{ 
                      endAdornment: <Typography color="text.secondary">km</Typography>
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* SEÇÃO: Datas */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem' }}>
                <CalendarToday /> Datas
              </Typography>
              <Grid container spacing={2.5}>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Data Entrada *"
                    type="date"
                    required
                    size="medium"
                    value={formulario.data_entrada}
                    onChange={(e) => setFormulario({ ...formulario, data_entrada: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Data Prevista Saída"
                    type="date"
                    size="medium"
                    value={formulario.data_prevista_saida}
                    onChange={(e) => setFormulario({ ...formulario, data_prevista_saida: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Data Saída"
                    type="date"
                    size="medium"
                    value={formulario.data_saida}
                    onChange={(e) => setFormulario({ ...formulario, data_saida: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    helperText="Preencher quando concluída"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* SEÇÃO: Oficina */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem' }}>
                <Build /> Oficina
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nome da Oficina"
                    size="medium"
                    value={formulario.oficina_nome}
                    onChange={(e) => setFormulario({ ...formulario, oficina_nome: e.target.value })}
                    placeholder="Ex: Oficina do João"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contato Oficina"
                    size="medium"
                    value={formulario.oficina_contato}
                    onChange={(e) => setFormulario({ ...formulario, oficina_contato: e.target.value })}
                    placeholder="(92) 99999-9999"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* SEÇÃO: Descrição */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem' }}>
                <Edit /> Descrição
              </Typography>
              <Grid container spacing={2.5}>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descrição *"
                    multiline
                    rows={2}
                    required
                    size="medium"
                    value={formulario.descricao}
                    onChange={(e) => setFormulario({ ...formulario, descricao: e.target.value })}
                    placeholder="Ex: Troca de embreagem"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: '1rem'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Problema Reportado"
                    multiline
                    rows={2}
                    size="medium"
                    value={formulario.problema_reportado}
                    onChange={(e) => setFormulario({ ...formulario, problema_reportado: e.target.value })}
                    placeholder="Descreva o problema identificado"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: '1rem'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Serviços Realizados"
                    multiline
                    rows={3}
                    size="medium"
                    value={formulario.servicos_realizados}
                    onChange={(e) => setFormulario({ ...formulario, servicos_realizados: e.target.value })}
                    placeholder="Descreva os serviços que foram ou serão realizados"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: '1rem'
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* SEÇÃO: Custos */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem' }}>
                <MonetizationOn /> Custos
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Custo Mão de Obra"
                    type="number"
                    size="medium"
                    value={formulario.custo_mao_obra}
                    onChange={(e) => setFormulario({ ...formulario, custo_mao_obra: parseFloat(e.target.value) || 0 })}
                    InputProps={{ 
                      startAdornment: <Typography color="text.secondary" sx={{ mr: 1 }}>R$</Typography>
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Custo Peças"
                    type="number"
                    size="medium"
                    value={formulario.custo_pecas}
                    onChange={(e) => setFormulario({ ...formulario, custo_pecas: parseFloat(e.target.value) || 0 })}
                    InputProps={{ 
                      startAdornment: <Typography color="text.secondary" sx={{ mr: 1 }}>R$</Typography>
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Outros Custos"
                    type="number"
                    size="medium"
                    value={formulario.custo_outros}
                    onChange={(e) => setFormulario({ ...formulario, custo_outros: parseFloat(e.target.value) || 0 })}
                    InputProps={{ 
                      startAdornment: <Typography color="text.secondary" sx={{ mr: 1 }}>R$</Typography>
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'error.main', 
                    borderRadius: 2, 
                    textAlign: 'center',
                    boxShadow: 2
                  }}>
                    <Typography variant="caption" color="white" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                      💰 CUSTO TOTAL
                    </Typography>
                    <Typography variant="h5" color="white" fontWeight="bold">
                      {formatarValor(
                        parseFloat(formulario.custo_mao_obra || 0) + 
                        parseFloat(formulario.custo_pecas || 0) + 
                        parseFloat(formulario.custo_outros || 0)
                      )}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* SEÇÃO: Quilometragem */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.1rem' }}>
                <Schedule /> Quilometragem & Revisão
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Próxima Revisão (KM)"
                    type="number"
                    size="medium"
                    value={formulario.proxima_revisao_km}
                    onChange={(e) => setFormulario({ ...formulario, proxima_revisao_km: e.target.value })}
                    InputProps={{ 
                      endAdornment: <Typography color="text.secondary">km</Typography>
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box sx={{ 
            p: 3, 
            borderTop: '2px solid #eee', 
            bgcolor: 'grey.50', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            position: 'sticky',
            bottom: 0,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
            zIndex: 1
          }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
              * Campos obrigatórios: Veículo, Descrição e Data de Entrada
            </Typography>
            <Box display="flex" gap={2}>
              <Button 
                variant="outlined" 
                onClick={() => setModalAberto(false)} 
                size="large"
                sx={{ minWidth: 120 }}
              >
                Cancelar
              </Button>
              <Button 
                variant="contained" 
                onClick={salvarManutencao} 
                disabled={!formulario.veiculo_id || !formulario.descricao || !formulario.data_entrada}
                size="large"
                startIcon={modoEdicao ? <Edit /> : <Add />}
                sx={{ 
                  minWidth: 200,
                  bgcolor: '#f78b00',
                  '&:hover': {
                    bgcolor: '#d67700'
                  }
                }}
              >
                {modoEdicao ? 'Atualizar Manutenção' : 'Criar Manutenção'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* MODAL HISTÓRICO DO VEÍCULO */}
      <Modal open={modalHistoricoAberto} onClose={() => setModalHistoricoAberto(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '90%', md: 900 },
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24
        }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #eee' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <History /> Histórico de Manutenções
              </Typography>
              <IconButton onClick={() => setModalHistoricoAberto(false)}>
                <Close />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ p: 3 }}>
            {estatisticasVeiculo && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Total</Typography>
                      <Typography variant="h6" fontWeight="bold">{estatisticasVeiculo.total_manutencoes}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Custo Total</Typography>
                      <Typography variant="h6" fontWeight="bold" color="error">{formatarValor(estatisticasVeiculo.custo_total)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Média Dias</Typography>
                      <Typography variant="h6" fontWeight="bold" color="warning.main">{parseFloat(estatisticasVeiculo.media_dias_parado || 0).toFixed(1)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Em Andamento</Typography>
                      <Typography variant="h6" fontWeight="bold" color="warning.main">{estatisticasVeiculo.em_andamento}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            <Divider sx={{ my: 2 }} />

            {historicoVeiculo.length === 0 ? (
              <Alert severity="info">Nenhuma manutenção encontrada para este veículo.</Alert>
            ) : (
              historicoVeiculo.map((manutencao, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start">
                      <Box>
                        <Box display="flex" gap={1} mb={1}>
                          <Chip label={manutencao.tipo} color="primary" size="small" />
                          <Chip label={manutencao.status} color={statusOptions.find(s => s.value === manutencao.status)?.color} size="small" />
                        </Box>
                        <Typography variant="body1" fontWeight="bold">{manutencao.descricao}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatarData(manutencao.data_entrada)} - {formatarData(manutencao.data_saida)} ({manutencao.dias_parado} dias)
                        </Typography>
                      </Box>
                      <Typography variant="h6" fontWeight="bold" color="error">
                        {formatarValor(manutencao.custo_total)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>

          <Box sx={{ p: 3, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => setModalHistoricoAberto(false)}>
              Fechar
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* MODAL CONFIRMAÇÃO EXCLUSÃO */}
      <Modal open={modalExcluirAberto} onClose={() => setModalExcluirAberto(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 400 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24
        }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #eee' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
              <Delete /> Confirmar Exclusão
            </Typography>
          </Box>
          
          <Box sx={{ p: 3 }}>
            <Typography variant="body1" gutterBottom>
              Tem certeza que deseja excluir esta manutenção?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esta ação não pode ser desfeita.
            </Typography>
            
            {manutencaoSelecionada && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Veículo</Typography>
                <Typography variant="body2" fontWeight="bold">{manutencaoSelecionada.veiculo_placa}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Descrição</Typography>
                <Typography variant="body2">{manutencaoSelecionada.descricao}</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ p: 3, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={() => setModalExcluirAberto(false)}>
              Cancelar
            </Button>
            <Button variant="contained" color="error" startIcon={<Delete />} onClick={excluirManutencao}>
              Excluir
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* MODAL FINALIZAR MANUTENÇÃO */}
      <Modal open={modalFinalizarAberto} onClose={() => setModalFinalizarAberto(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 700 },
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24
        }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #eee', bgcolor: 'success.main', color: 'white' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle /> Finalizar Manutenção
            </Typography>
            <Typography variant="caption">
              Complete os dados para finalizar a manutenção e liberar o veículo
            </Typography>
          </Box>
          
          <Box sx={{ p: 3 }}>
            {manutencaoSelecionada && (
              <>
                <Alert severity="info" icon={<DirectionsCar />} sx={{ mb: 3, borderRadius: 2 }}>
                  <Typography variant="body1" fontWeight="bold" gutterBottom>
                    {manutencaoSelecionada.veiculo_placa} - {manutencaoSelecionada.veiculo_modelo}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {manutencaoSelecionada.tipo} • {manutencaoSelecionada.descricao}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Entrada: {formatarData(manutencaoSelecionada.data_entrada)}
                  </Typography>
                </Alert>

                {/* Data de Saída */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday fontSize="small" /> Data de Conclusão
                  </Typography>
                  <TextField
                    fullWidth
                    label="Data de Saída *"
                    type="date"
                    required
                    InputLabelProps={{ shrink: true }}
                    value={formulario.data_saida}
                    onChange={(e) => setFormulario({ ...formulario, data_saida: e.target.value })}
                    sx={{ mt: 1, bgcolor: 'white' }}
                  />
                </Box>

                {/* Serviços Realizados */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Build fontSize="small" /> Serviços Executados
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Descreva detalhadamente todos os serviços que foram realizados na manutenção..."
                    value={formulario.servicos_realizados}
                    onChange={(e) => setFormulario({ ...formulario, servicos_realizados: e.target.value })}
                    sx={{ mt: 1, bgcolor: 'white' }}
                  />
                </Box>

                {/* Custos */}
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MonetizationOn fontSize="small" /> Detalhamento de Custos
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Mão de Obra"
                        type="number"
                        InputProps={{ 
                          startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>R$</Typography>,
                          sx: { bgcolor: 'white' }
                        }}
                        value={formulario.custo_mao_obra}
                        onChange={(e) => setFormulario({ ...formulario, custo_mao_obra: e.target.value })}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Peças"
                        type="number"
                        InputProps={{ 
                          startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>R$</Typography>,
                          sx: { bgcolor: 'white' }
                        }}
                        value={formulario.custo_pecas}
                        onChange={(e) => setFormulario({ ...formulario, custo_pecas: e.target.value })}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Outros"
                        type="number"
                        InputProps={{ 
                          startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>R$</Typography>,
                          sx: { bgcolor: 'white' }
                        }}
                        value={formulario.custo_outros}
                        onChange={(e) => setFormulario({ ...formulario, custo_outros: e.target.value })}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 2, p: 2, bgcolor: 'error.main', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="white" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                      💰 CUSTO TOTAL DA MANUTENÇÃO
                    </Typography>
                    <Typography variant="h3" color="white" fontWeight="bold">
                      {formatarValor(
                        parseFloat(formulario.custo_mao_obra || 0) + 
                        parseFloat(formulario.custo_pecas || 0) + 
                        parseFloat(formulario.custo_outros || 0)
                      )}
                    </Typography>
                  </Box>
                </Box>

                {/* Integração Financeira */}
                <Box sx={{ p: 2, bgcolor: 'primary.main', borderRadius: 2, mb: 2 }}>
                  <Box display="flex" alignItems="flex-start" gap={2}>
                    <input
                      type="checkbox"
                      checked={criarDespesaFinanceira}
                      onChange={(e) => setCriarDespesaFinanceira(e.target.checked)}
                      style={{ width: 24, height: 24, cursor: 'pointer', marginTop: 4 }}
                    />
                    <Box flex={1}>
                      <Typography variant="body1" fontWeight="bold" color="white" gutterBottom>
                        💰 Criar lançamento financeiro
                      </Typography>
                      <Typography variant="body2" color="white">
                        Uma conta a pagar será criada automaticamente no módulo financeiro com todos os custos desta manutenção
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Alert severity="success" icon={<CheckCircle />} sx={{ borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    ✅ Veículo será liberado automaticamente
                  </Typography>
                  <Typography variant="caption">
                    Ao finalizar, o veículo <strong>{manutencaoSelecionada.veiculo_placa}</strong> voltará para o status 
                    <strong> DISPONÍVEL</strong> e poderá ser alugado novamente.
                  </Typography>
                </Alert>
              </>
            )}
          </Box>

          <Box sx={{ p: 3, borderTop: '2px solid #eee', bgcolor: 'grey.50', display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="outlined" onClick={() => setModalFinalizarAberto(false)} size="large">
              Cancelar
            </Button>
            <Button 
              variant="contained" 
              color="success" 
              startIcon={<CheckCircle />} 
              onClick={finalizarManutencao}
              disabled={!formulario.data_saida}
              size="large"
              sx={{ minWidth: 200 }}
            >
              Finalizar Manutenção
            </Button>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
}
